import Logger from '../Services/loggerService';
import { getIframeSecret } from '../utils/iframeSecret';

let accessToken = null;

export const setAccessToken = (token) => {
    accessToken = token || null;
};

export const getAccessToken = () => accessToken;

// Si VITE_API_URL está definida y ya incluye /api, usarla tal cual; de lo contrario, agregar /api
const baseUrl = import.meta.env.VITE_API_URL ?
    (import.meta.env.VITE_API_URL.includes('/api') ? import.meta.env.VITE_API_URL : `${import.meta.env.VITE_API_URL}/api`)
    : '/api';

const PRIVATE_LINK_ROUTES = ['/vinculacion-tercero', '/solicitud-datacredito'];

async function refreshAccessToken() {
    // No intentar refrescar si estamos en modo iFrame de Odoo
    const currentPath = window.location.pathname;
    const isZapSignRoute = currentPath === '/zapsign-form';
    const iframeSecret = getIframeSecret();

    const urlParams = new URLSearchParams(window.location.search);
    const prefillToken = urlParams.get('token');

    const isPrivateLinkRoute = PRIVATE_LINK_ROUTES.includes(currentPath);
    const privateLinkToken = urlParams.get('private_token');

    // Sesiones sin login (no tienen JWT que refrescar): iFrame de Odoo,
    // link de prefill de ZapSign (?token=) y links privados con private_token.
    // Intentar refrescar aquí solo genera ruido de 401 en el formulario del cliente.
    if ((isZapSignRoute && (iframeSecret || prefillToken)) || (isPrivateLinkRoute && privateLinkToken)) {
        Logger.info('[apiClient] Sesión sin login (iFrame/prefill), omitiendo refresh de token');
        return null;
    }

    try {
        const res = await fetch(`${baseUrl}/auth/refresh-token`, {
            method: 'POST',
            credentials: 'include'
        });
        if (!res.ok) return null;
        const data = await res.json();
        if (data?.token) {
            setAccessToken(data.token);
            return data.token;
        }
        return null;
    } catch (e) {
        Logger.error('[apiClient] Error refrescando token', e);
        return null;
    }
}

export async function apiFetch(input, init = {}) {
    const headers = new Headers(init.headers || {});
    let token = getAccessToken();

    // Si no hay token en memoria, intentar obtenerlo de cookies como respaldo
    if (!token && typeof document !== 'undefined') {
        try {
            const Cookies = (await import('js-cookie')).default;
            token = Cookies.get('token');
            if (token) {
                setAccessToken(token);
            }
        } catch (e) {
            // Si no se puede importar js-cookie, continuar sin token
        }
    }

    if (token) headers.set('Authorization', `Bearer ${token}`);

    // Soporte para iFrame Secret (Odoo)
    if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const iframeSecret = getIframeSecret();

        if (iframeSecret) {
            headers.set('X-IFrame-Secret', iframeSecret);
        }

        const privateLinkToken = urlParams.get('private_token');
        if (privateLinkToken) {
            headers.set('X-Private-Link-Token', privateLinkToken);
        }

        // Soporte para Prefill Token (UUID de la URL)
        const prefillToken = urlParams.get('token');
        if (prefillToken) {
            headers.set('X-Prefill-Token', prefillToken);
        }
    }

    const requestInit = {
        ...init,
        headers,
        credentials: 'include' // necesario para enviar/recibir cookie HttpOnly de refresh
    };

    // Normalizar la URL: si es una ruta relativa que no empieza por el baseUrl, anteponerlo
    let finalUrl = input;
    if (typeof input === 'string' && !input.startsWith('http') && !input.startsWith(baseUrl)) {
        // Asegurar que no haya dobles slashes
        const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
        const cleanInput = input.startsWith('/') ? input : `/${input}`;
        finalUrl = `${cleanBase}${cleanInput}`;
    }

    let response = await fetch(finalUrl, requestInit);

    if (response.status === 401 || response.status === 403) {
        // Intentar refrescar y reintentar una vez
        const newToken = await refreshAccessToken();
        if (newToken) {
            headers.set('Authorization', `Bearer ${newToken}`);
            response = await fetch(input, { ...requestInit, headers });
        }
    }

    // No loggear errores de red si los logs están deshabilitados
    // Los errores de red son parte del comportamiento normal del navegador
    // y se mostrarán en la consola del navegador de todas formas

    return response;
}

export default {
    apiFetch,
    setAccessToken,
    getAccessToken
};

